<template>
    <div
        v-if="open"
        class="image-lightbox"
        role="dialog"
        aria-modal="true"
        :aria-label="name || email || $t('Image')"
        @click="onBackdropClick"
    >
        <div class="image-lightbox-panel">
            <div class="image-lightbox-bar">
                <button
                    ref="closeButton"
                    class="image-lightbox-close"
                    type="button"
                    :title="$t('Close')"
                    :aria-label="$t('Close')"
                    @click.stop="$emit('close')"
                >
                    <svg-vue class="image-lightbox-close-icon" icon="font-awesome.times-light"></svg-vue>
                </button>
            </div>
            <div class="image-lightbox-stage">
                <img
                    v-if="displayUrl"
                    :src="displayUrl"
                    :alt="alt || name || ''"
                    class="image-lightbox-img"
                    draggable="false"
                />
            </div>
            <div v-if="name || email" class="image-lightbox-caption">
                <div v-if="name" class="image-lightbox-name">{{ name }}</div>
                <div v-if="email" class="image-lightbox-email">{{ email }}</div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'image-lightbox',
    props: {
        open: {
            type: Boolean,
            default: false,
        },
        url: {
            type: String,
            default: '',
        },
        name: {
            type: String,
            default: '',
        },
        email: {
            type: String,
            default: '',
        },
        alt: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            scrollLocked: false,
        };
    },
    computed: {
        displayUrl() {
            if (!this.url) {
                return '';
            }
            // Gravatar disimpan dengan s=80; minta resolusi tinggi untuk popup
            // agar tidak di-upscale (blur).
            if (/gravatar\.com/i.test(this.url)) {
                return this.url.replace(/([?&])s=\d+/, '$1s=512');
            }
            return this.url;
        },
    },
    watch: {
        open(value) {
            if (value) {
                this.moveToBody();
                this.activate();
            } else {
                this.deactivate();
            }
        },
    },
    mounted() {
        if (this.open) {
            this.moveToBody();
            this.activate();
        }
    },
    beforeDestroy() {
        this.deactivate();
    },
    methods: {
        // Lightbox dipindahkan ke <body> agar posisi fixed-nya tidak terganggu
        // ancestor di dalam halaman (container overflow/scroll, transform,
        // -webkit-overflow-scrolling) — terutama di drawer mobile.
        moveToBody() {
            this.$nextTick(() => {
                if (
                    this.$el &&
                    this.$el.nodeType === 1 &&
                    this.$el.parentNode !== document.body
                ) {
                    document.body.appendChild(this.$el);
                }
            });
        },
        activate() {
            this.lockScroll();
            document.addEventListener('keydown', this.onKeydown);
            this.$nextTick(() => {
                if (this.$refs.closeButton) {
                    this.$refs.closeButton.focus();
                }
            });
        },
        deactivate() {
            document.removeEventListener('keydown', this.onKeydown);
            if (this.scrollLocked) {
                this.scrollLocked = false;
                document.body.style.overflow = '';
            }
        },
        lockScroll() {
            if (this.scrollLocked) {
                return;
            }
            this.scrollLocked = true;
            document.body.style.overflow = 'hidden';
        },
        onKeydown(event) {
            if (event.key === 'Escape') {
                this.$emit('close');
            }
        },
        onBackdropClick(event) {
            const target = event.target;
            if (
                target.closest('.image-lightbox-img') ||
                target.closest('.image-lightbox-caption')
            ) {
                return;
            }
            this.$emit('close');
        },
    },
};
</script>

<style scoped>
/* Layout: bar (tombol close) berdiri sendiri DI ATAS gambar — ruangnya
   dicadangkan, jadi tombol tidak pernah menimpa gambar pada ukuran layar apa pun. */
.image-lightbox {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 1rem 1rem;
    background: rgba(15, 23, 42, 0.82);
    -webkit-backdrop-filter: blur(8px);
    backdrop-filter: blur(8px);
    cursor: zoom-out;
    animation: image-lightbox-fade 0.18s ease-out;
}

.image-lightbox-panel {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 72rem;
    height: 100%;
    animation: image-lightbox-zoom 0.2s ease-out;
}

.image-lightbox-bar {
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 0.6rem 0;
}

.image-lightbox-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 9999px;
    border: 1px solid rgba(255, 255, 255, 0.28);
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    cursor: pointer;
    transition: background-color 0.15s ease, transform 0.15s ease;
}

.image-lightbox-close:hover {
    background: rgba(255, 255, 255, 0.22);
}

.image-lightbox-close:active {
    transform: scale(0.94);
}

.image-lightbox-close:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.8);
    outline-offset: 2px;
}

.image-lightbox-close-icon {
    width: 1.25rem;
    height: 1.25rem;
}

.image-lightbox-stage {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Ukuran natural: tidak pernah di-stretch (w-full), hanya dibatasi viewport.
   Avatar kecil tetap tajam; gambar besar menyesuaikan ruang yang tersedia. */
.image-lightbox-img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: 0.85rem;
    box-shadow: 0 24px 80px rgba(0, 0, 0, 0.45);
    outline: 1px solid rgba(255, 255, 255, 0.14);
    outline-offset: -1px;
    user-select: none;
    cursor: default;
}

.image-lightbox-caption {
    flex-shrink: 0;
    margin-top: 0.85rem;
    max-width: 100%;
    text-align: center;
    overflow-wrap: anywhere;
    cursor: default;
}

.image-lightbox-name {
    color: #fff;
    font-size: 0.9rem;
    font-weight: 600;
}

.image-lightbox-email {
    margin-top: 0.1rem;
    color: rgba(226, 232, 240, 0.85);
    font-size: 0.8rem;
}

@keyframes image-lightbox-fade {
    from {
        opacity: 0;
    }
}

@keyframes image-lightbox-zoom {
    from {
        opacity: 0;
        transform: scale(0.96) translateY(6px);
    }
}

@media (max-width: 640px) {
    .image-lightbox {
        padding: 0 0.75rem 0.75rem;
    }

    .image-lightbox-bar {
        padding: 0.35rem 0;
    }

    .image-lightbox-close {
        width: 2.25rem;
        height: 2.25rem;
    }

    .image-lightbox-name {
        font-size: 0.85rem;
    }

    .image-lightbox-email {
        font-size: 0.75rem;
    }
}

@media (prefers-reduced-motion: reduce) {
    .image-lightbox,
    .image-lightbox-panel {
        animation: none;
    }

    .image-lightbox-close {
        transition: none;
    }
}
</style>
